<template>
  <div>
    <div class="pl-5 pr-5">
      <!---->
      <h3>
        {{ $t("inventory.bulk-update.text") }}
      </h3>

      <hr />

      <h5>
        {{ $t("inventory.filter.plural") }}
      </h5>

      <b-alert v-if="hasFilter" show variant="warning">
        {{ $t("inventory.bulk-update.filter-warning") }}

        <ul>
          <li v-for="(i, index) in criteriaItems" :key="'criteria-item' + index">
            <b>{{ i.text }} :</b> {{ i.value | translate }}
          </li>
        </ul>
      </b-alert>
      <b-alert v-else show variant="secondary">
        {{ $t("inventory.bulk-update.no-filter-warning") }}
      </b-alert>

      <InventoryFilter id="sidebar-inventory-bulk-update-filter" @apply="applyFilter" />
      <!---->

      <hr />

      <h5 class="mt-3">{{ $t("inventory.bulk-update.edit-option.text") }}</h5>

      <b-row>
        <b-col md="3">
          <select v-model="editOption" class="form-control form-control-sm">
            <option value="ADD">{{ $t("inventory.bulk-update.edit-option.add") }}</option>
            <option value="REMOVE">{{ $t("inventory.bulk-update.edit-option.remove") }}</option>
            <option value="OVERRIDE">{{ $t("inventory.bulk-update.edit-option.override") }}</option>
          </select>
        </b-col>
        <b-col>
          <i>
            <span v-if="editOption == 'ADD'">
              {{ $t("inventory.bulk-update.edit-option.add-description") }}
            </span>
            <span v-else-if="editOption == 'REMOVE'">
              {{ $t("inventory.bulk-update.edit-option.remove-description") }}
            </span>
            <span v-else-if="editOption == 'OVERRIDE'">
              {{ $t("inventory.bulk-update.edit-option.override-description") }}
            </span>
          </i>
        </b-col>
      </b-row>

      <!---->
      <hr />
      <!-- 
        <h5 class="mt-4">
          {{ $t("inventory.filter.inventory-features") }}
        </h5> -->

      <b-card no-body class="shadow-none mb-1">
        <h4 class="p-3 hand text-muted m-0" v-b-toggle.datasource-inventory-accordion>
          <span class="when-closed align-middle"> <i class="bx bx-chevron-right"></i> </span>
          <span class="when-opened align-middle"> <i class="bx bx-chevron-down"></i> </span>

          {{ $t("inventory.datasource-features") }}
        </h4>
        <b-collapse id="datasource-inventory-accordion" class="border-top border-light" :visible="false" accordion="accordion">
          <b-card-body>
            <!--Direct Collect Method-->

            <h5 class="mb-1">{{ $t("direct-collect-method.text") }}</h5>

            <div v-for="item in selectedDirectCollectMethods" :key="item._id" class="mb-1">
              <i class="bx bx-trash text-danger hand" @click="removeDirectCollectMethod(item)"></i> {{ item.name | translate }}
            </div>
            <RightPanelSelect
              id="directCollectRef"
              ref="directCollectRef"
              :title="$t('direct-collect-method.text')"
              :text="addButtonText"
              store="directCollectMethod"
              :multiple="true"
              :selected="selectedDirectCollectMethods"
              @onSelected="directCollectMethodSelected"
            >
              <right-panel
                class="float-right"
                id="sidebar-new-direct-collect-method"
                width="800px"
                :icon="'mdi mdi-plus'"
                :text="$t('direct-collect-method.new')"
                @hidden="rightPanelClosed"
              >
                <div class="pl-5 pr-5">
                  <FormDirectCollect @onFormSave="onDirectCollectMethodFormSaved" :hide-library="true" />
                </div>
              </right-panel>
            </RightPanelSelect>

            <!--Other Collect Method-->

            <h5 class="mb-1 mt-4">{{ $t("other-collect-method.text") }}</h5>

            <div v-for="item in selectedOtherCollectMethods" :key="item._id" class="mt-1">
              <i class="bx bx-trash text-danger hand" @click="removeOtherCollectMethod(item)"></i> {{ item.name | translate }}
            </div>
            <RightPanelSelect
              id="otherCollectRef"
              ref="otherCollectRef"
              :title="$t('other-collect-method.text')"
              :text="addButtonText"
              store="otherCollectMethod"
              :multiple="true"
              :selected="selectedOtherCollectMethods"
              @onSelected="otherCollectMethodSelected"
            >
              <right-panel
                class="float-right"
                id="sidebar-new-other-collect-method"
                width="800px"
                :icon="'mdi mdi-plus'"
                :text="$t('other-collect-method.new')"
                @hidden="rightPanelClosed"
              >
                <div class="pl-5 pr-5">
                  <FormOtherCollect @onFormSave="onOtherCollectMethodFormSaved" :hide-library="true" />
                </div>
              </right-panel>
            </RightPanelSelect>

            <!--Physical Storage-->
            <h5 class="mb-1 mt-4">{{ $t("physical-storage.text") }}</h5>
            <div v-if="editOption != 'REMOVE'">
              <div class="bg-white" v-if="selectedPhysicalStoragesMethods.length > 0">
                <table class="table table-bordered table-striped">
                  <thead class="bg-secondary text-white">
                    <tr>
                      <td colspan="3">
                        {{ $t("physical-storage.text") }}
                      </td>
                      <td>
                        {{ $t("physical-storage.period") }}
                      </td>
                      <td>
                        {{ $t("physical-storage.since") }}
                      </td>
                      <td>
                        {{ $t("physical-storage.legal-basis") }}
                      </td>
                      <td>
                        {{ $t("common.description") }}
                      </td>
                    </tr>
                  </thead>

                  <tr v-for="item in selectedPhysicalStoragesMethods" :key="item._id">
                    <td>
                      <i class="bx bx-trash text-danger hand" @click="removePhysicalStorage(item)"></i>
                    </td>
                    <td>
                      <i class="bx bx-pencil text-warning hand" @click="editPhysicalStorage(item)"></i>
                    </td>
                    <td>
                      {{ item.libraryItem.name | translate }}
                    </td>
                    <td>
                      {{ item.period }}
                    </td>
                    <td>
                      {{ item.since }}
                    </td>
                    <td>
                      {{ item.legalBasis }}
                    </td>
                    <td>
                      {{ item.description }}
                    </td>
                  </tr>
                </table>
              </div>

              <span class="text-primary hand" @click="addPhysicalStorage"> <i class="bx bx-plus"></i> {{ addButtonText }} </span>

              <right-panel id="physicalStorageRightPanel" width="600px">
                <div class="pl-5 pr-5">
                  <DatasourcePhysicalStorageForm id="physicalStorageRightPanel" :prepareMode="true" @onSaved="onPhysicalStoragePrepared" />
                </div>
              </right-panel>
            </div>

            <div v-else>
              <div v-for="item in selectedPhysicalStoragesMethods" :key="item._id">
                <i class="bx bx-trash text-danger hand" @click="removePhysicalStorage(item)"></i>
                {{ item.libraryItem.name | translate }}
              </div>

              <RightPanelSelect
                id="physicalStorageRightPanel"
                :title="$t('physical-storage.text')"
                :text="addButtonText"
                store="physicalStorage"
                :multiple="true"
                :selected="selectedPhysicalStoragesMethods"
                @onSelected="onPhysicalStoragesSelected"
              >
              </RightPanelSelect>
            </div>

            <!--Electronic Storage-->
            <h5 class="mb-1 mt-4">{{ $t("electronic-storage.text") }}</h5>
            <div v-if="editOption != 'REMOVE'">
              <div class="bg-white" v-if="selectedElectronicStoragesMethods.length > 0">
                <table class="table table-bordered table-striped">
                  <thead class="bg-secondary text-white">
                    <tr>
                      <td colspan="3">
                        {{ $t("electronic-storage.text") }}
                      </td>
                      <td>
                        {{ $t("electronic-storage.period") }}
                      </td>
                      <td>
                        {{ $t("electronic-storage.since") }}
                      </td>
                      <td>
                        {{ $t("electronic-storage.legal-basis") }}
                      </td>
                      <td>
                        {{ $t("common.description") }}
                      </td>
                    </tr>
                  </thead>

                  <tr v-for="item in selectedElectronicStoragesMethods" :key="item._id">
                    <td>
                      <i class="bx bx-trash text-danger hand" @click="removeElectronicStorage(item)"></i>
                    </td>
                    <td>
                      <i class="bx bx-pencil text-warning hand" @click="editElectronicStorage(item)"></i>
                    </td>
                    <td>
                      {{ item.libraryItem.name | translate }}
                    </td>
                    <td>
                      {{ item.period }}
                    </td>
                    <td>
                      {{ item.since }}
                    </td>
                    <td>
                      {{ item.legalBasis }}
                    </td>
                    <td>
                      {{ item.description }}
                    </td>
                  </tr>
                </table>
              </div>

              <span class="text-primary hand" @click="addElectronicStorage"> <i class="bx bx-plus"></i> {{ addButtonText }} </span>

              <right-panel id="electronicStorageRightPanel" width="600px">
                <div class="pl-5 pr-5">
                  <DatasourceElectronicStorageForm id="electronicStorageRightPanel" :prepareMode="true" @onSaved="onElectronicStoragePrepared" />
                </div>
              </right-panel>
            </div>

            <div v-else>
              <div v-for="item in selectedElectronicStoragesMethods" :key="item._id">
                <i class="bx bx-trash text-danger hand" @click="removeElectronicStorage(item)"></i>
                {{ item.libraryItem.name | translate }}
              </div>

              <RightPanelSelect
                id="electronicStorageRightPanel"
                :title="$t('electronic-storage.text')"
                :text="addButtonText"
                store="electronicStorage"
                :multiple="true"
                :selected="selectedElectronicStoragesMethods"
                @onSelected="onElectronicStoragesSelected"
              />
            </div>

            <!--Physical Archive-->

            <h5 class="mb-1 mt-4">{{ $t("physical-archive.text") }}</h5>

            <div v-for="item in selectedPhysicalArchives" :key="item._id" class="mt-1">
              <i class="bx bx-trash text-danger hand" @click="removePhysicalArchive(item)"></i> {{ item.name | translate }}
            </div>
            <RightPanelSelect
              id="physicalArchiveRef"
              ref="physicalArchiveRef"
              :title="$t('physical-archive.text')"
              :text="addButtonText"
              store="physicalArchive"
              :multiple="true"
              :selected="selectedPhysicalArchives"
              @onSelected="physicalArchiveSelected"
            >
              <right-panel class="float-right" id="sidebar-new-physical-archive" width="800px" :icon="'mdi mdi-plus'" :text="$t('physical-archive.new')" @hidden="rightPanelClosed">
                <div class="pl-5 pr-5">
                  <FormPhysicalArchive @onFormSave="onPhysicalArchiveFormSaved" :inventory="true" :hide-library="true" />
                </div>
              </right-panel>
            </RightPanelSelect>
            <!--Electronic Archive-->

            <h5 class="mb-1 mt-4">{{ $t("electronic-archive.text") }}</h5>

            <div v-for="item in selectedElectronicArchives" :key="item._id" class="mt-1">
              <i class="bx bx-trash text-danger hand" @click="removeElectronicArchive(item)"></i> {{ item.name | translate }}
            </div>
            <RightPanelSelect
              id="electronicArchiveRef"
              ref="electronicArchiveRef"
              :title="$t('electronic-archive.text')"
              :text="addButtonText"
              store="electronicArchive"
              :multiple="true"
              :selected="selectedElectronicArchives"
              @onSelected="electronicArchiveSelected"
            >
              <right-panel
                class="float-right"
                id="sidebar-new-electronic-archive"
                width="800px"
                :icon="'mdi mdi-plus'"
                :text="$t('electronic-archive.new')"
                @hidden="rightPanelClosed"
              >
                <div class="pl-5 pr-5">
                  <FormElectronicArchive @onFormSave="onElectronicArchiveFormSaved" :inventory="true" :hide-library="true" />
                </div>
              </right-panel>
            </RightPanelSelect>

            <!---->
          </b-card-body>
        </b-collapse>
      </b-card>

      <b-card no-body class="shadow-none mb-1">
        <h4 class="p-3 hand text-muted m-0" v-b-toggle.data-inventory-accordion>
          <span class="when-closed align-middle"> <i class="bx bx-chevron-right"></i> </span>
          <span class="when-opened align-middle"> <i class="bx bx-chevron-down"></i> </span>

          {{ $t("inventory.data-features") }}
        </h4>
        <b-collapse id="data-inventory-accordion" class="border-top border-light" :visible="false" accordion="accordion">
          <b-card-body>
            <!--Access And Restrictions-->

            <h5 class="mb-1">{{ $t("access-restriction.text") }}</h5>

            <div v-if="editOption != 'REMOVE'">
              <div class="bg-white" v-if="selectedAccessAndRestrictions.length > 0">
                <div>
                  <table class="table table-bordered table-striped">
                    <thead class="bg-secondary text-white">
                      <tr>
                        <td colspan="3">
                          {{ $t("accessing-persons.name") }}
                        </td>
                        <td>
                          {{ $t("access-restriction.permission") }}
                        </td>
                      </tr>
                    </thead>

                    <tr v-for="item in selectedAccessAndRestrictions" :key="item._id">
                      <td>
                        <i class="bx bx-trash text-danger hand" @click="removeAccessAndRestriction(item)"></i>
                      </td>
                      <td>
                        <i class="bx bx-pencil text-warning hand" @click="editAccessAndRestriction(item)"></i>
                      </td>
                      <td>
                        {{ item.accessor.name | translate }}
                      </td>
                      <td>
                        {{ item.restriction.name | translate }}
                      </td>
                    </tr>
                  </table>
                </div>
              </div>

              <span class="text-primary hand" @click="addAccessAndRestriction"> <i class="bx bx-plus"></i> {{ addButtonText }} </span>

              <right-panel id="accessAndRestrictionRightPanel" width="600px">
                <div class="pl-5 pr-5">
                  <DataAccessAndRestrictionForm id="accessAndRestrictionRightPanel" :prepareMode="true" @onSaved="onAccessAndRestrictionPrepared" />
                </div>
              </right-panel>
            </div>

            <div v-else>
              <div v-for="item in selectedAccessAndRestrictions" :key="item._id" class="mb-1">
                <i class="bx bx-trash text-danger hand" @click="removeAccessAndRestriction(item)"></i> {{ item.accessor.name | translate }}
              </div>

              <RightPanelSelect
                id="accessAndRestrictionRightPanel"
                :title="$t('access-restriction.text')"
                :text="addButtonText"
                store="accessor"
                :multiple="true"
                :selected="selectedAccessors"
                @onSelected="onAccessorsSelected"
              />
            </div>

            <!--Disposal Method-->

            <h5 class="mb-1 mt-3">{{ $t("disposal-method.text") }}</h5>

            <div v-for="item in selectedDisposalMethods" :key="item._id" class="mb-1">
              <i class="bx bx-trash text-danger hand" @click="removeDisposalMethod(item)"></i> {{ item.name | translate }}
            </div>
            <RightPanelSelect
              id="disposalMethodRef"
              ref="disposalMethodRef"
              :title="$t('disposal-method.text')"
              :text="addButtonText"
              store="disposalMethod"
              :multiple="true"
              :selected="selectedDisposalMethods"
              @onSelected="disposalMethodSelected"
            >
              <right-panel class="float-right" id="sidebar-new-disposal-method" width="800px" :icon="'mdi mdi-plus'" :text="$t('disposal-method.new')" @hidden="rightPanelClosed">
                <div class="pl-5 pr-5">
                  <FormDisposalMethod @onFormSave="onDisposalMethodFormSaved" :hide-library="true" />
                </div>
              </right-panel>
            </RightPanelSelect>

            <!--Disposal Period-->

            <h5 class="mb-1 mt-3">
              {{ $t("disposal-method.periodic") }} <span class="text-muted">({{ $t("common.month") }})</span>
            </h5>

            <input v-model="disposalPeriod" class="form-control" type="number" min="0" max="360" />
          </b-card-body>
        </b-collapse>
      </b-card>

      <b-card no-body class="shadow-none">
        <h4 class="p-3 hand text-muted m-0" v-b-toggle.datasubject-inventory-accordion>
          <span class="when-closed align-middle"> <i class="bx bx-chevron-right"></i> </span>
          <span class="when-opened align-middle"> <i class="bx bx-chevron-down"></i> </span>

          {{ $t("inventory.datasubject-features") }}
        </h4>
        <b-collapse id="datasubject-inventory-accordion" class="border-top border-light" :visible="false" accordion="accordion">
          <b-card-body>
            <!--Processing Legal-->

            <h5 class="mb-1">{{ $t("processing.text") }}</h5>

            <div>
              <div class="bg-white" v-if="selectedProcessingLegals.length > 0">
                <div>
                  <table class="table table-bordered table-striped">
                    <thead class="bg-secondary text-white">
                      <tr>
                        <td colspan="2">
                          {{ $t("processing-transfering.purpose.text") }}
                        </td>
                        <td>
                          {{ $t("common.aquivalent-verbis") }}
                        </td>
                        <td>
                          {{ $t("processing.legal-reason") }}
                        </td>
                        <td>
                          {{ $t("common.regulation") }}
                        </td>

                        <td>
                          {{ $t("processing-transfering.legitimate-interest.text") }}
                        </td>
                      </tr>
                    </thead>

                    <tr v-for="item in selectedProcessingLegals" :key="item._id">
                      <td>
                        <i class="bx bx-trash text-danger hand" @click="removeProcessingLegal(item)"></i>
                      </td>
                      <td>
                        {{ item.purpose.name | translate }}
                      </td>
                      <td>
                        <span v-if="item.purpose.isVerbisPurpose">
                          {{ item.purpose.name | translate }}
                        </span>
                        <span v-else-if="!item.purpose.isVerbisPurpose && item.purpose.verbisAquivalent">{{ item.purpose.verbisAquivalent.name | translate }}</span>
                      </td>
                      <td>
                        {{ item.legalReason.name | translate }}
                        <!-- <span v-if="item.legalReason.gdprRegulation || item.legalReason.kvkkRegulation">
                            <br />
                            <i class="text-muted">({{ item.legalReason | regulation | translate }})</i>
                          </span> -->
                      </td>
                      <td>
                        <span v-if="item.legalReason.gdprRegulation || item.legalReason.kvkkRegulation">
                          {{ item.legalReason | regulation | translate }}
                        </span>
                        <span v-if="item.legalReason.regulation">
                          {{ item.legalReason.regulation | translate }}
                        </span>
                      </td>

                      <td>
                        <span v-if="item.legitimateInterest">
                          {{ item.legitimateInterest | translate }}
                        </span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>

              <span class="text-primary hand" @click="addProcessingLegal"> <i class="bx bx-plus"></i> {{ addButtonText }} </span>

              <right-panel id="processingLegalRightPanel" width="1200px" @hidden="rightPanelClosed">
                <div class="pl-5 pr-5">
                  <DatasubjectProcessingLegalForm id="processingLegalRightPanel" :prepareMode="true" @onSaved="onProcessingLegalPrepared" />
                </div>
              </right-panel>
            </div>

            <!-- <div v-else>
                <div v-for="item in selectedProcessingLegals" :key="item._id" class="mb-1">
                  <i class="bx bx-trash text-danger hand" @click="removeProcessingLegal(item)"></i> {{ item.legalReason.name | translate }}
                </div>

                <RightPanelSelect
                  id="processingLegalRightPanel"
                  :title="$t('processing.legal-reason.text')"
                  :text="addButtonText"
                  store="legalReason"
                  :multiple="true"
                  :selected="selectedProcessingLegalReasons"
                  @onSelected="onProcessingLegalReasonSelected"
                />
              </div> -->

            <!--Transfering-->

            <h5 class="mb-1 mt-3">{{ $t("transfering.text") }}</h5>
            <div v-if="editOption != 'REMOVE'">
              <div class="bg-white" v-if="selectedTransferings.length > 0">
                <div>
                  <table class="table table-bordered table-striped">
                    <thead class="bg-secondary text-white">
                      <tr>
                        <td colspan="3">
                          {{ $t("thirdparty.text") }}
                        </td>
                        <td>
                          {{ $t("processing-transfering.reason-and-interest") }}
                        </td>

                        <td>
                          {{ $t("transfering.method.plural") }}
                        </td>
                        <td>
                          {{ $t("transfering.prevention.plural") }}
                        </td>
                        <td>
                          {{ $t("transfering.note") }}
                        </td>
                      </tr>
                    </thead>

                    <tr v-for="transfering in selectedTransferings" :key="transfering._id">
                      <td>
                        <i class="bx bx-trash text-danger hand" @click="removeTransfering(transfering)"></i>
                      </td>
                      <td>
                        <i class="bx bx-pencil text-warning hand" @click="editTransfering(transfering)"></i>
                      </td>
                      <td>
                        {{ transfering.thirdParty.name | translate }}
                        <br />
                        <i class="text-muted">({{ transfering.thirdParty.receiverGroup.name | translate }})</i>
                      </td>

                      <td class="p-0">
                        <table v-if="transfering.legals.length > 0" class="table m-0">
                          <thead class="bg-dark text-white">
                            <tr>
                              <th style="width: 20%;">{{ $t("processing-transfering.purpose.text") }}</th>
                              <th style="width: 50%;">{{ $t("processing-transfering.legal-reason.text") }}</th>
                              <th style="width: 15%;">{{ $t("common.regulation") }}</th>
                              <th style="width: 20%;">{{ $t("processing-transfering.legitimate-interest.text") }}</th>
                            </tr>
                          </thead>
                          <tr v-for="i in transfering.legals" :key="i.legalReason.libraryItem._id + i.purpose.libraryItem._id">
                            <td>{{ i.purpose.name | translate }}</td>
                            <td>{{ i.legalReason.name | translate }}</td>
                            <td>{{ i.legalReason.regulation | translate }}</td>
                            <td>
                              {{ i.legitimateInterest | translate }}
                            </td>
                          </tr>
                        </table>
                      </td>
                      <td>
                        <ul class="m-0 p-0">
                          <li v-for="item in transfering.methods" :key="item._id">
                            {{ item.name | translate }}
                          </li>
                        </ul>
                      </td>
                      <td>
                        <ul class="m-0 p-0">
                          <li v-for="item in transfering.preventions" :key="item._id">
                            {{ item.name | translate }}
                          </li>
                        </ul>
                      </td>
                      <td>
                        {{ transfering.note | translate }}
                      </td>
                    </tr>
                  </table>
                </div>
              </div>

              <span class="text-primary hand" @click="addTransfering"> <i class="bx bx-plus"></i> {{ addButtonText }} </span>

              <right-panel id="transferingRightPanel" width="1250px" @hidden="rightPanelClosed">
                <div class="pl-5 pr-5">
                  <DatasubjectTransferingForm id="transferingRightPanel" :prepareMode="true" @onSaved="onTransferingPrepared" />
                </div>
              </right-panel>
            </div>

            <div v-else>
              <div v-for="item in selectedTransferings" :key="item._id">
                <i class="bx bx-trash text-danger hand" @click="removeTransfering(item)"></i>
                {{ item.thirdParty.name | translate }}
              </div>

              <RightPanelSelect
                id="thirdPartyRightPanel"
                :title="$t('thirdparty.text')"
                :text="addButtonText"
                store="organizationThirdParty"
                :multiple="true"
                :selected="selectedThirdParties"
                @onSelected="onThirdPartySelected"
              />
            </div>

            <!--Datasubject Rights-->

            <h5 class="mb-1 mt-3">{{ $t("datasubject-right.text") }}</h5>

            <div v-for="item in selectedDatasubjectRights" :key="item._id" class="mb-1">
              <i class="bx bx-trash text-danger hand" @click="removeDatasubjectRight(item)"></i> {{ item.name | translate }}
            </div>
            <RightPanelSelect
              id="datasubjectRightRef"
              ref="datasubjectRightRef"
              :title="$t('datasubject-right.text')"
              :text="addButtonText"
              store="datasubjectRight"
              :multiple="true"
              :selected="selectedDatasubjectRights"
              @onSelected="datasubjectRightSelected"
            >
              <right-panel
                class="float-right"
                id="sidebar-new-datasubject-right"
                width="600px"
                :icon="'mdi mdi-plus'"
                :text="$t('datasubject-right.new')"
                @hidden="rightPanelClosed"
              >
                <div class="pl-5 pr-5">
                  <FormDatasubjectRight @onFormSave="onDatasubjectRightFormSaved" :hide-library="true" />
                </div>
              </right-panel>
            </RightPanelSelect>

            <!--Auto Processing-->

            <h5 class="mb-1 mt-3">{{ $t("processing.auto-alt") }}</h5>

            <select v-model="autoProcessing" class="form-control">
              <option :value="undefined">{{ $t("common.choose") }}</option>
              <option value="YES">{{ $t("common.yes") }}</option>
              <option value="NO">{{ $t("common.no") }}</option>
            </select>
          </b-card-body>
        </b-collapse>
      </b-card>

      <!--Save-->
      <sweet-button @click="update()" :disabled="isOrganizationBulkUpdating" variant="success" icon="bx bx-cog" buttonClass="mt-5 mb-4">
        <span v-if="!isOrganizationBulkUpdating">
          {{ $t("inventory.bulk-update.update") }}
        </span>
        <span v-else>
          {{ $t("inventory.bulk-update.409") }}
        </span>
      </sweet-button>

      <sweet-button @click="resetForm()" variant="secondary" icon="bx bx-x" buttonClass="mt-5 mb-4 float-right">
        {{ $t("common.clear") }}
      </sweet-button>

      <!---->
    </div>
    <!---->
  </div>
</template>

<script>
import RightPanelSelect from "@c/right-panel-select";
import FormElectronicArchive from "@modules/archive-environments/electronic-archive/workspace-library-electronic-archive-form";
import FormPhysicalArchive from "@modules/archive-environments/physical-archive/workspace-library-physical-archive-form";
import FormDirectCollect from "@modules/collect-method/workspace-library-direct-collect-method-form";
import FormOtherCollect from "@modules/collect-method/workspace-library-other-collect-method-form";
import FormDatasubjectRight from "@modules/datasubject-right/workspace-library-datasubject-right-form";
import DataAccessAndRestrictionForm from "@modules/inventory/component/access-and-restriction/data-access-and-restriction-form";
import DatasourceElectronicStorageForm from "@modules/inventory/component/electronic-storage/datasource-electronic-storage-form";
import InventoryFilter from "@modules/inventory/component/filter/inventory-filter";
import DatasourcePhysicalStorageForm from "@modules/inventory/component/physical-storage/datasource-physical-storage-form";
import DatasubjectProcessingLegalForm from "@modules/inventory/component/processing/datasubject-legal-form";
import DatasubjectTransferingForm from "@modules/inventory/component/transfering/datasubject-transfering-form";
import FormDisposalMethod from "@modules/storage-environments/disposal-method/workspace-library-disposal-method-form";

export default {
  components: {
    InventoryFilter,
    RightPanelSelect,
    DatasourcePhysicalStorageForm,
    DatasubjectTransferingForm,
    DatasourceElectronicStorageForm,
    DatasubjectProcessingLegalForm,
    DataAccessAndRestrictionForm,
    RightPanelSelect,
    FormDirectCollect,
    FormOtherCollect,
    FormPhysicalArchive,
    FormElectronicArchive,
    FormDisposalMethod,
    FormDatasubjectRight,
  },
  computed: {
    isOrganizationBulkUpdating() {
      return !!this.currentOrganization?.isBulkUpdating;
    },
    addButtonText() {
      if (this.editOption == "REMOVE") {
        return this.$t("inventory.bulk-update.button-type.remove");
      } else if (this.editOption == "OVERRIDE") {
        return this.$t("inventory.bulk-update.button-type.override");
      }
      return this.$t("inventory.bulk-update.button-type.add");
    },

    editOption: {
      get() {
        return this.updateForm.editOption;
      },
      set(value) {
        this.$store.commit("gdprInventoryBulkUpdate/editOption", value);
      },
    },

    disposalPeriod: {
      get() {
        return this.updateForm.disposalPeriod;
      },
      set(value) {
        this.$store.commit("gdprInventoryBulkUpdate/disposalPeriod", value);
      },
    },
    autoProcessing: {
      get() {
        return this.updateForm.autoProcessing;
      },
      set(value) {
        this.$store.commit("gdprInventoryBulkUpdate/autoProcessing", value);
      },
    },

    selectedDirectCollectMethods() {
      return this.updateForm.directCollectMethods;
    },

    selectedOtherCollectMethods() {
      return this.updateForm.otherCollectMethods;
    },

    selectedPhysicalStoragesMethods() {
      return this.updateForm.physicalStorages;
    },

    selectedElectronicStoragesMethods() {
      return this.updateForm.electronicStorages;
    },

    selectedPhysicalArchives() {
      return this.updateForm.physicalArchives;
    },
    selectedElectronicArchives() {
      return this.updateForm.electronicArchives;
    },

    selectedProcessingPurposes() {
      return this.updateForm.processingPurposes;
    },

    selectedProcessingLegals() {
      return this.updateForm.processingLegals;
    },
    selectedProcessingLegalReasons() {
      return this.updateForm.processingLegals.map((i) => i.legalReason);
    },

    selectedAccessAndRestrictions() {
      return this.updateForm.accessAndRestrictions;
    },
    selectedAccessors() {
      return this.updateForm.accessAndRestrictions.map((i) => i.accessor);
    },

    selectedTransferings() {
      return this.updateForm.transferings;
    },
    selectedThirdParties() {
      return this.updateForm.transferings.map((i) => i.thirdParty);
    },

    selectedDatasubjectRights() {
      return this.updateForm.datasubjectRights;
    },

    selectedDisposalMethods() {
      return this.updateForm.disposalMethods;
    },

    //
    updateForm() {
      return this.$store.getters["gdprInventoryBulkUpdate/updateForm"];
    },

    criteria() {
      return this.$store.getters["inventoryFilter/criteria"];
    },
    hasFilter() {
      return Object.keys(this.criteria).find((i) => !!this.criteria[i]);
    },
    criteriaItems() {
      return Object.keys(this.criteria)
        .map((i) => {
          return !this.criteria[i] ? null : { text: this.filterNameMap[i], value: this.criteria[i].name };
        })
        .filter((i) => !!i);
    },
  },

  data() {
    return {
      filterNameMap: {
        organization: this.$t("legal-document-template.filters.organization"),
        department: this.$t("legal-document-template.filters.department"),
        process: this.$t("legal-document-template.filters.process"),
        datasource: this.$t("legal-document-template.filters.datasource"),
        dataCategory: this.$t("legal-document-template.filters.data-category"),
        data: this.$t("legal-document-template.filters.data"),
        datasubject: this.$t("legal-document-template.filters.datasubject"),
        directCollectMethod: this.$t("legal-document-template.filters.direct-collection-method"),
        otherCollectMethod: this.$t("legal-document-template.filters.other-collection-method"),
        electronicStorage: this.$t("legal-document-template.filters.electronic-storage"),
        physicalStorage: this.$t("legal-document-template.filters.physical-storage"),
        transferingPurpose: this.$t("legal-document-template.filters.transfering-purpose"),
        transferingLegalReason: this.$t("legal-document-template.filters.transfering-legal-reason"),
        transferingMethod: this.$t("legal-document-template.filters.transfering-method"),
        receiverGroup: this.$t("legal-document-template.filters.receiver-group"),
        thirdParty: this.$t("legal-document-template.filters.third-party"),
      },
    };
  },

  methods: {
    directCollectMethodSelected(items) {
      this.$store.commit("gdprInventoryBulkUpdate/setDirectCollectMethods", items);
    },

    removeDirectCollectMethod(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removeDirectCollectMethod", item);
    },

    //
    otherCollectMethodSelected(items) {
      this.$store.commit("gdprInventoryBulkUpdate/setOtherCollectMethods", items);
    },

    removeOtherCollectMethod(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removeOtherCollectMethod", item);
    },

    //
    physicalArchiveSelected(items) {
      this.$store.commit("gdprInventoryBulkUpdate/setPhysicalArchives", items);
    },

    removePhysicalArchive(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removePhysicalArchive", item);
    },

    //
    electronicArchiveSelected(items) {
      this.$store.commit("gdprInventoryBulkUpdate/setElectronicArchives", items);
    },

    removeElectronicArchive(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removeElectronicArchive", item);
    },

    //

    onAccessAndRestrictionPrepared(item) {
      const li = item.libraryItem;
      this.$store.commit("gdprInventoryBulkUpdate/addAccessAndRestriction", {
        _id: li._id,
        libraryItem: { _id: li._id, name: li.name },
        accessor: { _id: li._id, name: li.name },
        restriction: { _id: item.restriction._id, name: item.restriction.name },
      });
    },
    onAccessorsSelected(items) {
      this.$store.commit(
        "gdprInventoryBulkUpdate/setAccessAndRestrictions",
        items.map((item) => ({
          _id: item._id,
          libraryItem: { _id: item._id },
          accessor: { _id: item._id, name: item.name },
          restriction: {},
        }))
      );
    },
    addAccessAndRestriction() {
      this.$store.commit("dataAccessAndRestriction/editedAccessAndRestriction", {});
      this.$root.$emit("bv::toggle::collapse", "accessAndRestrictionRightPanel");
    },
    editAccessAndRestriction(item) {
      this.$store.commit("dataAccessAndRestriction/editedAccessAndRestriction", item);
      this.$root.$emit("bv::toggle::collapse", "accessAndRestrictionRightPanel");
    },

    removeAccessAndRestriction(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removeAccessAndRestriction", item);
    },
    //
    onProcessingLegalPrepared(items) {
      items.forEach((item) => {
        this.$store.commit("gdprInventoryBulkUpdate/addProcessingLegal", {
          purpose: item.purpose,
          legalReason: item.legalReason,
          legitimateInterest: item.legitimateInterest,
        });
      });
    },
    onProcessingLegalReasonSelected(items) {
      this.$store.commit(
        "gdprInventoryBulkUpdate/setProcessingLegals",
        items.map((item) => ({
          purpose: item.purpose,
          legalReason: item.legalReason,
          legitimateInterest: item.legitimateInterest,
        }))
      );
    },
    addProcessingLegal() {
      this.$store.commit("datasubjectProcessingLegal/editedDatasubjectProcessingLegal", null);
      this.$root.$emit("bv::toggle::collapse", "processingLegalRightPanel");
    },

    removeProcessingLegal(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removeProcessingLegal", item);
    },
    //
    disposalMethodSelected(items) {
      this.$store.commit("gdprInventoryBulkUpdate/setDisposalMethods", items);
    },

    removeDisposalMethod(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removeDisposalMethod", item);
    },

    //
    datasubjectRightSelected(items) {
      this.$store.commit("gdprInventoryBulkUpdate/setDatasubjectRights", items);
    },

    removeDatasubjectRight(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removeDatasubjectRight", item);
    },

    //
    addTransfering() {
      this.$store.commit("datasubjectTransfering/editedDatasubjectTransfering", {
        purposes: [],
        legals: [],
        methods: [],
        preventions: [],
        note: null,
      });
      this.$root.$emit("bv::toggle::collapse", "transferingRightPanel");
    },

    onTransferingPrepared(item) {
      const thirdParties = Array.isArray(item.thirdParty) ? item.thirdParty : [item.thirdParty];
      for (const tp of thirdParties) {
        this.$store.commit("gdprInventoryBulkUpdate/addTransfering", {
          _id: tp._id,
          name: tp.name,
          ...item,
          thirdParty: tp,
        });
      }
    },

    editTransfering(item) {
      this.$store.commit("datasubjectTransfering/editedDatasubjectTransfering", item);
      this.$root.$emit("bv::toggle::collapse", "transferingRightPanel");
    },

    removeTransfering(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removeTransfering", item);
    },

    onThirdPartySelected(items) {
      this.$store.commit(
        "gdprInventoryBulkUpdate/setTransferings",
        items.map((item) => ({
          _id: item._id,
          name: item.name,
          thirdParty: item,
          purposes: [],
          legals: [],
          methods: [],
          preventions: [],
        }))
      );
    },
    //
    onPhysicalStoragePrepared(item) {
      const libraryItems = Array.isArray(item.libraryItem) ? item.libraryItem : [item.libraryItem];
      for (const element of libraryItems) {
        this.$store.commit("gdprInventoryBulkUpdate/addPhysicalStorage", {
          _id: item.libraryItem._id,
          libraryItem: { _id: element._id, name: element.name },
          period: item.period,
          description: item.description,
          legalBasis: item.legalBasis,
          since: item.since,
        });
      }
    },
    onPhysicalStoragesSelected(items) {
      this.$store.commit(
        "gdprInventoryBulkUpdate/setPhysicalStorages",
        items.map((item) => ({
          libraryItem: { _id: item._id, name: item.name },
          _id: item._id,
          name: item.name,
          period: 12,
        }))
      );
    },
    addPhysicalStorage() {
      this.$store.commit("datasourcePhysicalStorage/editedPhysicalStorage", {});
      this.$root.$emit("bv::toggle::collapse", "physicalStorageRightPanel");
    },
    editPhysicalStorage(storage) {
      this.$store.commit("datasourcePhysicalStorage/editedPhysicalStorage", storage);
      this.$root.$emit("bv::toggle::collapse", "physicalStorageRightPanel");
    },

    removePhysicalStorage(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removePhysicalStorage", item);
    },

    onElectronicStoragePrepared(item) {
      const libraryItems = Array.isArray(item.libraryItem) ? item.libraryItem : [item.libraryItem];
      for (const element of libraryItems) {
        this.$store.commit("gdprInventoryBulkUpdate/addElectronicStorage", {
          _id: item.libraryItem._id,
          libraryItem: { _id: element._id, name: element.name },
          period: item.period,
          description: item.description,
          legalBasis: item.legalBasis,
          since: item.since,
        });
      }
    },
    onElectronicStoragesSelected(items) {
      this.$store.commit(
        "gdprInventoryBulkUpdate/setElectronicStorages",
        items.map((item) => ({
          libraryItem: { _id: item._id, name: item.name },
          _id: item._id,
          name: item.name,
          period: 12,
        }))
      );
    },
    addElectronicStorage() {
      this.$store.commit("datasourceElectronicStorage/editedElectronicStorage", {});
      this.$root.$emit("bv::toggle::collapse", "electronicStorageRightPanel");
    },
    editElectronicStorage(storage) {
      this.$store.commit("datasourceElectronicStorage/editedElectronicStorage", storage);
      this.$root.$emit("bv::toggle::collapse", "electronicStorageRightPanel");
    },

    removeElectronicStorage(item) {
      this.$store.commit("gdprInventoryBulkUpdate/removeElectronicStorage", item);
    },
    //

    async update() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        const form = {
          editOption: this.updateForm.editOption,
          payload: { ...this.updateForm },
          criteria: this.criteria,
        };
        await this.$store.dispatch("gdprInventoryBulkUpdate/update", form);
        this.$emit("onUpdated");

        this.resetForm();
        this.toggleSidebar();

        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        if (error.status == 409) {
          this.$handleHttpErrorByStatusCode(error, { customMessage: this.$t("inventory.bulk-update.409") });
        } else {
          this.$handleHttpErrorByStatusCode(error);
        }
      }
    },
    toggleSidebar() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-inventory-bulk-update");
    },
    resetForm() {
      this.$store.commit("gdprInventoryBulkUpdate/reset");
    },
    applyFilter() {
      this.$emit("applyFilter");
    },
    rightPanelClosed() {
      this.$store.commit("datasubjectProcessingLegal/showRecommendations", false);
      this.$store.commit("datasubjectTransfering/showRecommendations", false);

      this.$store.commit("datasubjectTransfering/editedDatasubjectTransfering", null);
      this.$store.commit("datasubjectTransfering/editedDatasubject", null);

      const previousReceiverGroup = this.$store.getters["inventoryFilter/criteria"].previousReceiverGroup;
      this.$store.commit("inventoryFilter/receiverGroup", previousReceiverGroup);
      this.$store.commit("inventoryFilter/previousReceiverGroup", undefined);
    },

    rightPanelClosedForm(id) {
      this.$root.$emit("bv::toggle::collapse", id);
    },
    paginateForm(refValue) {
      this.$refs[refValue].reload();
    },

    onDirectCollectMethodFormSaved() {
      this.rightPanelClosedForm("sidebar-new-direct-collect-method");
      this.paginateForm("directCollectRef");
    },
    onOtherCollectMethodFormSaved() {
      this.rightPanelClosedForm("sidebar-new-other-collect-method");
      this.paginateForm("otherCollectRef");
    },
    onPhysicalArchiveFormSaved() {
      this.rightPanelClosedForm("sidebar-new-physical-archive");
      this.paginateForm("physicalArchiveRef");
    },
    onElectronicArchiveFormSaved() {
      this.rightPanelClosedForm("sidebar-new-electronic-archive");
      this.paginateForm("electronicArchiveRef");
    },
    onDisposalMethodFormSaved() {
      this.rightPanelClosedForm("sidebar-new-disposal-method");
      this.paginateForm("disposalMethodRef");
    },

    onDatasubjectRightFormSaved() {
      this.rightPanelClosedForm("sidebar-new-datasubject-right");
      this.paginateForm("datasubjectRightRef");
    },
  },
};
</script>

<style scoped>
.when-opened,
.when-closed {
  font-size: 20px;
}
.collapsed > .when-opened,
:not(.collapsed) > .when-closed {
  display: none;
}
</style>
